import { createRoute, Link } from "@tanstack/react-router";
import { RootRoute } from "../root-route";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { LocationPrompt } from "@/components/location-prompt";
import { ClientOnly } from "@/components/client-only";
import { SwedenMapSvg } from "@/components/sweden-map-svg";
import { StatusCard } from "@/components/status-card";
import { EventsFeed } from "@/components/events-feed";
import { getAllKommunStatus, getNotices, type KommunStatus } from "@/lib/api";
import { RESTRIKTION_LABEL, RESTRIKTION_CLASSES, inForceRows, worstRestriktionTyp } from "@/lib/status";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://bevattningskartan.se/#website",
      url: "https://bevattningskartan.se/",
      name: "Bevattningskartan",
      description:
        "Aktuella bevattningsförbud i Sverige på en publik karta.",
      inLanguage: "sv-SE",
    },
    {
      "@type": "Dataset",
      "@id": "https://bevattningskartan.se/#dataset",
      name: "Bevattningsförbud i Sverige",
      description:
        "Automatiskt insamlad data om bevattningsförbud från svenska VA-huvudmän. Uppdateras löpande.",
      url: "https://bevattningskartan.se/",
      inLanguage: "sv-SE",
      spatialCoverage: {
        "@type": "Place",
        name: "Sverige",
      },
      creator: {
        "@type": "Organization",
        name: "Bevattningskartan",
        url: "https://bevattningskartan.se/",
      },
      license: "https://creativecommons.org/licenses/by/4.0/",
      isAccessibleForFree: true,
    },
  ],
};

export const IndexRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/",
  component: Index,
  head: () => ({
    meta: [
      { title: `Bevattningsförbud ${new Date().getFullYear()} | Karta över aktuella bevattningsförbud i Sverige` },
      { name: "description", content: "Hitta aktuella bevattningsförbud i din kommun. Bevattningskartan visar alla bevattningsförbud i Sverige på en interaktiv karta. Uppdateras dagligen från kommunernas VA-aktörer." },
      { name: "keywords", content: `bevattningsförbud, bevattningsförbud Sverige, bevattningsförbud ${new Date().getFullYear()}, vattenförbud, bevattningskarta, bevattningsförbud kommun, trädgårdsbevattning förbud` },
      { property: "og:url", content: "https://bevattningskartan.se/" },
    ],
    links: [
      { rel: "canonical", href: "https://bevattningskartan.se/" },
      { rel: "alternate", hrefLang: "sv", href: "https://bevattningskartan.se/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(jsonLd),
      },
    ],
  }),
});

function Index() {
  const { data: rows = [] } = useQuery({
    queryKey: ["kommun-status"],
    queryFn: getAllKommunStatus,
  });

  const { data: events = [] } = useQuery({
    queryKey: ["notices"],
    queryFn: getNotices,
  });

  const [selectedKommunkod, setSelectedKommunkod] = useState<string | null>(null);

  const rowsByKommun = new Map<string, KommunStatus[]>();
  for (const r of rows) {
    const bucket = rowsByKommun.get(r.kommunkod) ?? [];
    bucket.push(r);
    rowsByKommun.set(r.kommunkod, bucket);
  }

  const selectedRows = selectedKommunkod ? (rowsByKommun.get(selectedKommunkod) ?? []) : [];

  const activeCount = new Set(
    rows.filter((r) => r.typ === "forbud" && r.period !== "kommande").map((r) => r.kommunkod)
  ).size;
  const kommandeCount = new Set(
    rows.filter((r) => r.period === "kommande" && r.restriktion_id).map((r) => r.kommunkod)
  ).size;
  const recommendationCount = [...rowsByKommun.values()].filter(
    (bucket) =>
      worstRestriktionTyp(
        inForceRows(bucket).map((r) => r.typ).filter((t): t is NonNullable<typeof t> => t !== null)
      ) === "rekommendation"
  ).length;
  const trackedCount = [...rowsByKommun.values()].filter((bucket) =>
    bucket.some((r) => r.kalla_id && r.senast_scrapad)
  ).length;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-8 sm:px-6 sm:pt-12">
        <LocationPrompt />

        <section className="mt-14">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-foreground">
                Kartan
              </h2>
              <p className="mt-1 text-muted-foreground">
                {activeCount > 0 ? (
                  <>
                    <span className="font-medium text-foreground">
                      {activeCount}
                    </span>{" "}
                    av {trackedCount} bevakade kommuner har aktivt förbud just
                    nu.
                  </>
                ) : (
                  <>
                    Inga aktiva bevattningsförbud bland våra{" "}
                    {trackedCount} bevakade kommuner just nu.
                  </>
                )}
                {recommendationCount > 0 && (
                  <>
                    {" "}
                    Ytterligare{" "}
                    <span className="font-medium text-foreground">
                      {recommendationCount}
                    </span>{" "}
                    har en rekommendation.
                  </>
                )}
                {kommandeCount > 0 && (
                  <>
                    {" "}
                    <span className="font-medium text-foreground">
                      {kommandeCount}
                    </span>{" "}
                    har ett kommande förbud eller en kommande rekommendation.
                  </>
                )}
              </p>
            </div>
            <div className="hidden flex-wrap items-center gap-3 text-xs text-muted-foreground sm:flex">
              {(["forbud", "rekommendation"] as const).map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${RESTRIKTION_CLASSES[t].dot}`}
                  />
                  {RESTRIKTION_LABEL[t]}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground sm:hidden">
            {(["forbud", "rekommendation"] as const).map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5">
                <span
                  className={`h-2.5 w-2.5 rounded-full ${RESTRIKTION_CLASSES[t].dot}`}
                />
                {RESTRIKTION_LABEL[t]}
              </span>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="min-w-0 overflow-hidden">
              <ClientOnly
                fallback={
                  <div className="h-[55vh] sm:h-[70vh] lg:h-[90vh] w-full animate-pulse rounded-2xl border border-border bg-muted/40" />
                }
              >
                <SwedenMapSvg
                  rows={rows}
                  selectedKommunkod={selectedKommunkod}
                  onSelect={(row: KommunStatus) => setSelectedKommunkod(row.kommunkod)}
                />
              </ClientOnly>
            </div>

            <aside className="space-y-6">
              {selectedRows.length > 0 ? (
                <StatusCard
                  kommunNamn={selectedRows[0].kommun_namn}
                  data={selectedRows}
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
                  Klicka på en markör i kartan för att se status, datum och
                  källa för kommunen.
                </div>
              )}

              <div>
                <h3 className="mb-3 font-display text-xl text-foreground">
                  Senaste händelser
                </h3>
                <EventsFeed
                  events={events}
                  onSelectKommun={(kod) => setSelectedKommunkod(kod)}
                />
              </div>
            </aside>
          </div>
        </section>

        <section className="mt-10">
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
            <h2 className="font-display text-2xl sm:text-3xl text-foreground">
              Aktiva förbud och rekommendationer
            </h2>
            <p className="mt-2 text-muted-foreground">
              Just nu har{" "}
              <span className="font-medium text-foreground">{activeCount}</span>{" "}
              kommuner aktivt bevattningsförbud och{" "}
              <span className="font-medium text-foreground">
                {recommendationCount}
              </span>{" "}
              har en rekommendation.
            </p>
            <Link
              to="/aktiva-forbud"
              className="mt-4 inline-block text-sm font-medium text-primary hover:underline"
            >
              Se alla aktiva förbud och rekommendationer →
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
